import AddTraitForm from "@/components/AddTraitForm";
import EditTraitDialog from "@/components/EditTraitDialog";
import TraitList from "@/components/TraitList";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useRetainedValue } from "@/hooks/useRetainedValue";
import { useToast } from "@/hooks/use-toast";
import { useEntryConfigs } from "@/hooks/useFirestore";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { useCreateEntryConfig, useUpdateEntryConfig, useDeleteEntryConfig, useCheckEntryConfigUsage, useArchiveEntryConfig, useRestoreEntryConfig } from "@/services/dataService";
import { EntryConfigUnion } from "@/types/entryConfig";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { filterEntryConfigsForUI } from "@/constants/entryConfigVisibility";
import TransitionOverlay from "@/components/TransitionOverlay";

const Traits = () => {
  const [isAddTraitOpen, setIsAddTraitOpen] = useState(false);
  const [isAddTraitDirty, setIsAddTraitDirty] = useState(false);
  // Warn before discarding an in-progress new trait (Escape, click-outside, X, Cancel).
  const addTraitGuard = useUnsavedChangesGuard({
    isDirty: isAddTraitDirty,
    onOpenChange: setIsAddTraitOpen,
  });
  const [isEditTraitOpen, setIsEditTraitOpen] = useState(false);
  const [selectedTrait, setSelectedTrait] = useState<EntryConfigUnion | null>(null);
  const [traitEditSession, setTraitEditSession] = useState(0);
  // The organization the Edit Trait dialog was opened in. Its usage check and
  // the delete/archive that check gates both run against this org, even if the
  // selection changes while the dialog is open.
  const [editTraitOrgId, setEditTraitOrgId] = useState<string | undefined>(undefined);
  const [deletionOverlay, setDeletionOverlay] = useState<{
    open: boolean;
    title: string;
    subtitle?: string;
  } | null>(null);
  const renderedSelectedTrait = useRetainedValue(selectedTrait);
  const { userRole, selectedOrganization } = useAuth();
  const { entryConfigs, loading, error } = useEntryConfigs();
  const createEntryConfig = useCreateEntryConfig();
  const updateEntryConfig = useUpdateEntryConfig();
  const deleteEntryConfig = useDeleteEntryConfig();
  const checkEntryConfigUsage = useCheckEntryConfigUsage();
  const archiveEntryConfig = useArchiveEntryConfig();
  const restoreEntryConfig = useRestoreEntryConfig();
  const { toast } = useToast();

  // Role-based permissions
  // Only editors and admins can create/edit/delete traits
  // Collectors have read-only access to traits (same as viewers)
  const canEdit = userRole && ["editor", "admin"].includes(userRole);
  const canDelete = userRole && ["editor", "admin"].includes(userRole);
  // Only admins can edit/delete system traits
  const isAdmin = userRole === "admin";

  const traitNames = useMemo(() => entryConfigs.map(e => e.name), [entryConfigs]);

  const handleAddTrait = async (
    newTrait: Omit<EntryConfigUnion, "id" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy">
  ) => {
    try {
      const createdEntryConfig = await createEntryConfig(newTrait);

      if (createdEntryConfig) {
        toast({
          title: "Trait added",
          description: `${newTrait.name} has been added successfully`,
        });
        setIsAddTraitOpen(false);
      } else {
        toast({
          title: "Error",
          description: "Failed to add trait",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding trait:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add trait",
        variant: "destructive",
      });
    }
  };

  const handleEditTrait = (trait: EntryConfigUnion) => {
    setTraitEditSession((current) => current + 1);
    setSelectedTrait(trait);
    setEditTraitOrgId(selectedOrganization?.id);
    setIsEditTraitOpen(true);
  };

  const handleCheckTraitUsage = (traitId: string) =>
    checkEntryConfigUsage(traitId, { orgId: editTraitOrgId });

  const handleUpdateTrait = async (
    updatedTrait: Omit<EntryConfigUnion, "id" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy">
  ) => {
    if (!selectedTrait?.id) return;

    try {
      const success = await updateEntryConfig(selectedTrait.id, updatedTrait);

      if (success) {
        toast({
          title: "Trait updated",
          description: `${updatedTrait.name} has been updated successfully`,
        });
        setIsEditTraitOpen(false);
        setSelectedTrait(null);
      } else {
        toast({
          title: "Error",
          description: "Failed to update trait",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating trait:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update trait",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTrait = async (traitId: string) => {
    // Find the trait name for the overlay message
    const trait = entryConfigs.find((t) => t.id === traitId);
    const traitName = trait?.name || "trait";

    // Show the overlay
    setDeletionOverlay({
      open: true,
      title: "Deleting trait...",
      subtitle: `Removing "${traitName}"...`,
    });

    try {
      const success = await deleteEntryConfig(traitId, { orgId: editTraitOrgId });

      if (success) {
        toast({
          title: "Trait deleted",
          description: "The trait has been deleted successfully",
        });
        // Close the outer Edit Trait dialog AFTER the delete resolves, so the
        // inner AlertDialog (already auto-closed by Radix on confirm) and the
        // outer Dialog don't unmount in the same tick — that double-close
        // strands pointer-events: none on document.body.
        setIsEditTraitOpen(false);
        setSelectedTrait(null);
      } else {
        toast({
          title: "Error",
          description: "Failed to delete trait",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting trait:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete trait",
        variant: "destructive",
      });
    } finally {
      setDeletionOverlay(null);
    }
  };

  const handleArchiveTrait = async (traitId: string) => {
    const trait = entryConfigs.find((t) => t.id === traitId);
    const traitName = trait?.name || "trait";

    setDeletionOverlay({
      open: true,
      title: "Archiving trait...",
      subtitle: `Archiving "${traitName}"...`,
    });

    try {
      const success = await archiveEntryConfig(traitId, { orgId: editTraitOrgId });

      if (success) {
        toast({
          title: "Trait archived",
          description: `"${traitName}" was archived. Existing data is preserved and still exports.`,
        });
        // Close the outer Edit Trait dialog AFTER the archive resolves (same
        // pointer-events discipline as delete).
        setIsEditTraitOpen(false);
        setSelectedTrait(null);
      } else {
        toast({
          title: "Error",
          description: "Failed to archive trait",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error archiving trait:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to archive trait",
        variant: "destructive",
      });
    } finally {
      setDeletionOverlay(null);
    }
  };

  const handleRestoreTrait = async (trait: EntryConfigUnion) => {
    if (!trait.id) return;
    const traitName = trait.name || "trait";

    setDeletionOverlay({
      open: true,
      title: "Restoring trait...",
      subtitle: `Restoring "${traitName}"...`,
    });

    try {
      const success = await restoreEntryConfig(trait.id);

      if (success) {
        toast({
          title: "Trait restored",
          description: `"${traitName}" is active again and available for new tasks.`,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to restore trait",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error restoring trait:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to restore trait",
        variant: "destructive",
      });
    } finally {
      setDeletionOverlay(null);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-64">
          <p>Loading traits...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-red-500">Error loading traits: {error.message}</p>
        </div>
      </div>
    );
  }

  // Separate active organization/system traits from archived ones.
  // Archived-exclusion lives here (and in the task pickers) — NOT in the shared
  // filterEntryConfigsForUI, which the export/historical paths rely on.
  const visibleEntryConfigs = filterEntryConfigsForUI(entryConfigs);
  const activeTraits = visibleEntryConfigs.filter(trait => !trait.isArchived);
  const archivedTraits = visibleEntryConfigs.filter(trait => trait.isArchived);

  const organizationTraits = activeTraits.filter(trait => !trait.isSystem);
  const systemTraits = activeTraits.filter(trait => trait.isSystem);
  const archivedOrgTraits = archivedTraits.filter(trait => !trait.isSystem);
  const archivedSystemTraits = archivedTraits.filter(trait => trait.isSystem);

  return (
    <div className="container mx-auto px-4 py-6">
      <TransitionOverlay
        open={deletionOverlay?.open ?? false}
        title={deletionOverlay?.title ?? ""}
        subtitle={deletionOverlay?.subtitle}
      />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Traits</h1>
        {canEdit && (
          <Button
            variant="default"
            onClick={() => setIsAddTraitOpen(true)}>
            <Plus className="mr-1" /> Add Trait
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Organization Traits</CardTitle>
          <CardDescription>Traits used across all trials and tasks in your organization</CardDescription>
        </CardHeader>
        <CardContent>
          <TraitList traits={organizationTraits} onEditTrait={handleEditTrait} canEdit={canEdit} />
        </CardContent>
      </Card>

      {systemTraits.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Glean AI Traits</CardTitle>
            <CardDescription>Pre-configured traits for specialized measurements</CardDescription>
          </CardHeader>
          <CardContent>
            <TraitList traits={systemTraits} onEditTrait={handleEditTrait} canEdit={isAdmin} />
          </CardContent>
        </Card>
      )}

      {archivedTraits.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Archived Traits</CardTitle>
            <CardDescription>
              Traits used in previous tasks that have been disabled for future use
            </CardDescription>
          </CardHeader>
          <CardContent>
            {archivedOrgTraits.length > 0 && (
              <TraitList
                traits={archivedOrgTraits}
                variant="archived"
                onRestoreTrait={handleRestoreTrait}
                canEdit={canDelete}
              />
            )}
            {archivedSystemTraits.length > 0 && (
              <div className={archivedOrgTraits.length > 0 ? "mt-6" : undefined}>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Glean AI Traits</h3>
                <TraitList
                  traits={archivedSystemTraits}
                  variant="archived"
                  onRestoreTrait={handleRestoreTrait}
                  canEdit={isAdmin}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={isAddTraitOpen}
        onOpenChange={addTraitGuard.guardedOnOpenChange}>
        <DialogContent className="w-[min(96vw,1180px)] max-w-none max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add New Trait</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-4 -mr-2">
            <AddTraitForm
              onSubmit={handleAddTrait}
              onCancel={addTraitGuard.requestClose}
              existingNames={traitNames}
              onDirtyChange={setIsAddTraitDirty}
            />
          </div>
        </DialogContent>
      </Dialog>

      {addTraitGuard.confirmation}

      <EditTraitDialog
        key={renderedSelectedTrait ? `${renderedSelectedTrait.id}-${traitEditSession}` : "no-trait-selected"}
        open={isEditTraitOpen}
        onOpenChange={setIsEditTraitOpen}
        trait={renderedSelectedTrait}
        onTraitUpdated={handleUpdateTrait}
        onTraitDeleted={handleDeleteTrait}
        onTraitArchived={handleArchiveTrait}
        onCheckTraitUsage={handleCheckTraitUsage}
        canDelete={canDelete}
        existingNames={traitNames}
      />
    </div>
  );
};

export default Traits;
